import { matchesSearch } from '@webbuilder/shared-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TEMPLATE_CATEGORIES, TEMPLATE_INDUSTRIES, taxonomyLabelKey } from '@webbuilder/schemas'
import { useI18n } from '../lib/i18n'
import type { Session } from '@supabase/supabase-js'
import {
  activateAdminTemplate,
  archiveAdminTemplate,
  createAdminTemplate,
  deleteAdminTemplate,
  deleteProject,
  getAccountAccess,
  listAdminTemplates,
  listDeletedProjects,
  listProjects,
  publishAdminTemplate,
  resetAdminTemplateDraft,
  restoreProject,
  type DeletedProject,
  updateAdminTemplateMeta,
  uploadAsset,
} from '../lib/api'
import { getDemoSite } from '../lib/api'
import type { AdminTemplate, Project, UserRole } from '../lib/api'
import { TemplateAdminCard } from '../components/TemplateAdminCard'
import { useTabThumb } from '../hooks/useTabThumb'
import { TemplateDataModal, type TemplateForm } from '../components/TemplateDataModal'
import { DashboardTopbar } from '../components/DashboardTopbar'
import { DashboardFooter } from '../components/DashboardFooter'
import { DashboardPageHeader } from '../components/DashboardPageHeader'
import { useToast } from '../components/Toast'
import { Workspace } from '../components/ProjectWorkspace/Workspace'
import { ADMIN_ONLY_SECTIONS, WorkspaceSidebar, type WorkspaceGlobalView, type WorkspaceSection } from '../components/ProjectWorkspace/WorkspaceSidebar'
import { StartProjectScreen } from '../components/StartProjectScreen'
import { TeamInviteBanner } from '../components/TeamInviteBanner'
import { DeletedProjectsBanner } from '../components/DeletedProjectsBanner'
import { TransferBanner } from '../components/TransferBanner'
import { ProjectPlans } from '../components/ProjectWorkspace/sections/ProjectPlans'
import { PlanLimitModal } from '../components/ProjectWorkspace/PlanLimitModal'
import { ADMIN_PROJECT_LIMIT, PROJECT_LIMITS, reachedLimit } from '../lib/planLimits'
import { useBillingPlan } from '../lib/useBillingPlan'
import type { ProjectAccessRole } from '@webbuilder/shared-types'
import { DashboardUserMenu } from '../components/DashboardUserMenu'
import { BootScreen } from '../components/BootScreen'
import { ProjectSwitchOverlay } from '../components/ProjectSwitchOverlay'
import { ProjectDeleteOverlay } from '../components/ProjectDeleteOverlay'
import { ProjectRestoreOverlay } from '../components/ProjectRestoreOverlay'
import { ConfirmDialog } from '../components/ProjectWorkspace/ConfirmDialog'
import { DashboardTopbarEnd } from '../components/DashboardTopbarActions'
import { AdminUsers } from '../components/DashboardSections/AdminUsers'
import { AdminAiUnmet } from '../components/DashboardSections/AdminAiUnmet'
import { AdminEmails } from '../components/DashboardSections/AdminEmails'
import { AdminImages } from '../components/DashboardSections/AdminImages'
import { WorkspaceSettings } from '../components/DashboardSections/WorkspaceSettings'
import { BillingLedger } from '../components/DashboardSections/BillingLedger'
import { AccountDomains } from '../components/DashboardSections/AccountDomains'
import { AccountUsage } from '../components/DashboardSections/AccountUsage'
import { AdminAuthCodes } from '../components/DashboardSections/AdminAuthCodes'
import { useTheme } from '../App'
import { useUnsavedRegistry } from '../lib/unsavedChanges'
import { CreateWithAiModal } from '../components/CreateWithAiModal/CreateWithAiModal'
import { CreateBlankModal } from '../components/CreateWithAiModal/CreateBlankModal'
import { clearDemoDesign, readDemoDesign } from '../lib/demoDesign'
import { consumePendingPrompt } from '../lib/pendingPrompt'
import { clearPendingSiteDesign, peekPendingSiteDesign, siteDesignHasContent, siteDesignLabel, siteExample, siteRecipe, takePendingSiteDesignArrival, withSiteDesign } from '../lib/siteDesign'
import { hydrateFromSite } from '../lib/preview/compose'
import { saveWelcome, welcomeDone } from '../lib/welcomeAnswers'
import { IconLayoutBoard, IconLoader2, IconPlus } from '@tabler/icons-react'
import { editorHref } from '../lib/editorSession'

/* Animated AI spark: a gradient 4-point star with two companion sparks. The main
   star breathes while the small ones twinkle on co-prime timelines (3.6s / 2.8s /
   4.4s, identical endpoints) → a seamless, never-repeating-in-sync infinite loop.
   All motion rides transform/opacity (GPU-composited). Styles: .dash-ai-spark in styles.css */


function toSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    || 'proyecto'
}

const DEFAULT_TEMPLATE_FORM: TemplateForm = {
  name: 'Nuevo template',
  slug: 'nuevo-template',
  kind: 'section',
  category: '',
  description: '',
  facets: {},
  isPremium: false,
  isPublic: false,
  thumbnailUrl: '',
}

// Categories that the project generator maps by aiTags (worker AI_SECTION_TYPES).
// Deriving aiTags from the chosen category keeps that generator working while the
// admin only manages the new taxonomy facets.
const CATEGORY_TO_AI_SECTION: Record<string, string> = {
  navbar: 'header',
  hero: 'hero',
  features: 'features',
  testimonials: 'testimonials',
  faq: 'faq',
  contact: 'contact',
  footer: 'footer',
}

type TemplateSort = 'recent' | 'oldest' | 'used' | 'updated' | 'name'
const TEMPLATE_SORTS: TemplateSort[] = ['recent', 'oldest', 'used', 'updated', 'name']

/**
 * Las partes de la biblioteca de bloques, como pestañas del admin. Antes era
 * una sola lista donde «Content» guardaba 22 cosas, 20 de ellas piezas de los
 * diseños de blog y CMS que nunca se insertan sueltas.
 */
const TEMPLATE_AREAS = ['sections', 'catalog', 'blog', 'cms', 'components'] as const
type TemplateArea = (typeof TEMPLATE_AREAS)[number]

const isBlogDesign = (category: string) => category === 'blog-index' || category === 'blog-post'

function templateAreaOf(template: AdminTemplate): TemplateArea {
  if (template.kind === 'page') return isBlogDesign(template.category) ? 'blog' : 'cms'
  if (template.partOf) return isBlogDesign(template.partOf.category) ? 'blog' : 'cms'
  const category = template.facets.category ?? template.category
  if (category === 'blog') return 'blog'
  if (category === 'commerce') return 'catalog'
  if (template.kind === 'component' || template.kind === 'header' || template.kind === 'footer' || category === 'banner') return 'components'
  return 'sections'
}

/**
 * Blog y CMS por grupos: primero las secciones sueltas y después cada diseño
 * de página seguido de sus bloques. Un bloque cuyo diseño no pasa el filtro
 * (un borrador con «Borradores») sigue saliendo bajo el nombre de su diseño.
 */
function templateGroups(list: AdminTemplate[]): { id: string; name: string | null; category: string | null; items: AdminTemplate[] }[] {
  const loose = list.filter((template) => template.kind !== 'page' && !template.partOf)
  const designs = new Map<string, { id: string; name: string; category: string; items: AdminTemplate[] }>()
  for (const template of list) {
    const design = template.kind === 'page' ? { id: template.id, name: template.name, category: template.category } : template.partOf
    if (!design) continue
    const group = designs.get(design.id) ?? { ...design, items: [] }
    if (template.kind === 'page') group.items.unshift(template)
    else group.items.push(template)
    designs.set(design.id, group)
  }
  const order = ['blog-index', 'blog-post', 'cms-collection', 'cms-item']
  return [
    ...(loose.length ? [{ id: 'loose', name: null, category: null, items: loose }] : []),
    ...[...designs.values()].sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category) || a.name.localeCompare(b.name)),
  ]
}

/** Cuándo salió: la fecha de publicación, o la de creación si nunca se publicó. */
const templateDate = (template: AdminTemplate) => Date.parse(template.publishedAt ?? template.createdAt ?? '') || 0

function sortTemplates(list: AdminTemplate[], sort: TemplateSort): AdminTemplate[] {
  const byName = (a: AdminTemplate, b: AdminTemplate) => a.name.localeCompare(b.name)
  const compare: Record<TemplateSort, (a: AdminTemplate, b: AdminTemplate) => number> = {
    recent: (a, b) => templateDate(b) - templateDate(a) || byName(a, b),
    oldest: (a, b) => templateDate(a) - templateDate(b) || byName(a, b),
    used: (a, b) => (b.usesCount ?? 0) - (a.usesCount ?? 0) || templateDate(b) - templateDate(a),
    updated: (a, b) => (Date.parse(b.updatedAt ?? '') || 0) - (Date.parse(a.updatedAt ?? '') || 0),
    name: byName,
  }
  return [...list].sort(compare[sort])
}

// ─── Dashboard navigation ↔ URL/history (browser back works) ──────────────────
type DashView = 'home' | 'contacts' | 'apps' | 'analytics' | 'templates' | 'siteTemplates' | 'images' | 'team' | 'settings' | 'billing' | 'domains' | 'plans' | 'usage' | 'payments' | 'users' | 'authCodes' | 'aiUnmet' | 'adminEmails'
const DASH_VIEWS: DashView[] = ['home', 'contacts', 'apps', 'analytics', 'templates', 'siteTemplates', 'images', 'team', 'settings', 'billing', 'domains', 'plans', 'usage', 'payments', 'users', 'authCodes', 'aiUnmet', 'adminEmails']
// `payouts` faltaba: recargar estando en Depósitos, o pegar su enlace, caía en
// Inicio. Lo que no esté en esta lista no se puede enlazar.
/* Las que una dirección puede abrir directamente. Las tres de Configuración
   —negocio, región y políticas— faltaban, así que recargar estando en ellas
   devolvía a Inicio; y son justo las que se enlazan entre sí.
   Faltaban también las dos vistas de Citas —`bookingsCalendar`, `bookingsList`—
   y Marca: son sitios a los que se llega desde otras pantallas, así que su
   dirección se pega y se recarga más que ninguna. La lista hay que ampliarla al añadir una sección;
   olvidarlo no rompe nada visible hasta que alguien recarga. */
const WORKSPACE_SECTIONS: WorkspaceSection[] = ['overview', 'contacts', 'pages', 'blog', 'cms', 'analytics', 'forms', 'emails', 'bookings', 'bookingsCalendar', 'bookingsList', 'emailMarketing', 'courses', 'payments', 'digitalProducts', 'physicalProducts', 'services', 'orders', 'coupons', 'shipping', 'payouts', 'salesAnalytics', 'bookingsAnalytics', 'team', 'config', 'general', 'brand', 'business', 'region', 'policies', 'publish', 'usage', 'backups', 'stripe', 'googleAnalytics', 'omnisend', 'openTable', 'calendars', 'collections']
const WORKSPACE_GLOBAL_VIEWS: WorkspaceGlobalView[] = ['settings', 'billing', 'domains', 'plans', 'usage', 'siteTemplates', 'templates', 'images', 'users', 'authCodes', 'aiUnmet', 'adminEmails']


/**
 * `record` es la ficha abierta dentro de una sección (un contacto, un mensaje).
 *
 * Sin él, abrir una ficha no dejaba entrada en el historial: el botón "atrás"
 * salía del proyecto en vez de volver a la lista que acababas de dejar.
 */
type DashNav = { view: DashView; projectId: string | null; section: WorkspaceSection; record: string | null }

function readDashNav(): DashNav {
  const p = new URLSearchParams(window.location.search)
  const view = p.get('view') as DashView | null
  const section = p.get('section') as WorkspaceSection | null
  return {
    view: view && DASH_VIEWS.includes(view) ? view : 'home',
    projectId: p.get('project') || null,
    section: section && WORKSPACE_SECTIONS.includes(section) ? section : 'overview',
    record: p.get('record') || null,
  }
}

function dashNavUrl(nav: DashNav): string {
  const p = new URLSearchParams()
  if (nav.projectId) {
    p.set('project', nav.projectId)
    if (nav.section !== 'overview') p.set('section', nav.section)
    if (nav.record) p.set('record', nav.record)
    if (WORKSPACE_GLOBAL_VIEWS.includes(nav.view as WorkspaceGlobalView)) p.set('view', nav.view)
  } else if (nav.view !== 'home') {
    p.set('view', nav.view)
  }
  const s = p.toString()
  return s ? `${window.location.pathname}?${s}` : window.location.pathname
}

function templateFormFromTemplate(template: AdminTemplate): TemplateForm {
  return {
    name: template.name,
    slug: template.slug,
    kind: template.kind as TemplateForm['kind'],
    category: template.category,
    description: template.description ?? '',
    facets: template.facets ?? {},
    isPremium: template.isPremium,
    isPublic: template.isPublic,
    thumbnailUrl: template.thumbnailUrl ?? '',
  }
}



// Stale-while-revalidate cache so the dashboard shows projects instantly instead
// of a skeleton. The module-level copy makes in-session navigation instant; the
// localStorage copy (keyed per user) survives full page reloads too. Either way we
// always refetch in the background and replace the stale data when it arrives.
const PROJECTS_CACHE_PREFIX = 'veltica:projects:'
const LAST_ACTIVE_PROJECT_PREFIX = 'veltica:last-active-project:'
let projectsCache: Project[] | null = null
let projectsCacheUserId: string | null = null

function readProjectsCache(userId: string): Project[] | null {
  if (projectsCache && projectsCacheUserId === userId) return projectsCache
  try {
    const raw = localStorage.getItem(PROJECTS_CACHE_PREFIX + userId)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Project[]
    projectsCache = parsed
    projectsCacheUserId = userId
    return parsed
  } catch {
    return null
  }
}

function writeProjectsCache(userId: string, data: Project[]) {
  projectsCache = data
  projectsCacheUserId = userId
  try {
    localStorage.setItem(PROJECTS_CACHE_PREFIX + userId, JSON.stringify(data))
  } catch {
    /* quota / private mode — module cache still works */
  }
}

function readLastActiveProject(userId: string): string | null {
  try {
    return localStorage.getItem(LAST_ACTIVE_PROJECT_PREFIX + userId)
  } catch {
    return null
  }
}

function writeLastActiveProject(userId: string, projectId: string) {
  try {
    localStorage.setItem(LAST_ACTIVE_PROJECT_PREFIX + userId, projectId)
  } catch {
    /* private mode — navigation still works for the current session */
  }
}

export function Home({ session }: { session: Session }) {
  const { t } = useI18n()
  const userId = session.user.id
  const [projects, setProjects] = useState<Project[]>(() => readProjectsCache(userId) ?? [])
  /** Si `projects` ya viene del servidor y no de la copia guardada. Ver el efecto
   *  que abre el proyecto: hasta entonces, «no está en la lista» no significa
   *  «no existe». */
  const listedRef = useRef(false)
  const [loading, setLoading] = useState(() => readProjectsCache(userId) === null)
  // True when the last projects fetch failed even after a token refresh — we keep
  // showing the cached list but surface a "reconnecting" banner instead of silence.
  const [reconnecting, setReconnecting] = useState(false)
  /* Si se está enseñando la pantalla de empezar proyecto por encima de un
     proyecto abierto. Sin proyectos es la pantalla de fondo y esto no hace
     falta: allí no hay nada debajo que tapar. */
  const [startProjectOpen, setStartProjectOpen] = useState(false)
  /** El proyecto con la cuenta atrás corriendo. Nada se ha borrado todavía. */
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null)
  /** Los que están en su ventana de gracia, para ofrecerlos en el selector. */
  const [deletedProjects, setDeletedProjects] = useState<DeletedProject[]>([])
  /** El que se está mirando en el diálogo de «esto se borró». */
  const [reviewingDeleted, setReviewingDeleted] = useState<DeletedProject | null>(null)
  /** El que se está recuperando, con su ceremonia. */
  const [restoring, setRestoring] = useState<DeletedProject | null>(null)
  /** El traspaso que se está mirando, abierto desde la banda del panel. */
  const [workspaceProjectId, setWorkspaceProjectId] = useState<string | null>(() => {
    const nav = readDashNav()
    if (nav.projectId) return nav.projectId
    /**
     * Las vistas que el taller SABE alojar no cierran el proyecto.
     *
     * Facturación, Ajustes o Ayuda existen en dos sitios: dentro del taller
     * —con su menú lateral y su marco— y en la pantalla sin proyectos. Con esta
     * comprobación en `!== 'home'`, llegar por enlace a `?view=billing` caía
     * siempre en la segunda: es lo que pasaba al volver del portal de Stripe,
     * que devuelve exactamente a esa dirección. Quien tiene proyectos veía su
     * facturación sin el panel alrededor, como si hubiera perdido el sitio.
     *
     * Con proyecto, el taller aloja la vista; sin ninguno, `cached` viene vacío
     * y se cae sola a la pantalla de siempre.
     */
    if (nav.view !== 'home' && !WORKSPACE_GLOBAL_VIEWS.includes(nav.view as WorkspaceGlobalView)) return null
    const cached = readProjectsCache(userId)
    const lastActive = readLastActiveProject(userId)
    if (lastActive && cached?.some((project) => project.id === lastActive)) return lastActive
    return cached?.[0]?.id ?? null
  })
  /**
   * El cambio de proyecto en curso, mientras dura la cortina.
   *
   * Los dos nombres se guardan AQUÍ y no se leen de la lista dentro de la
   * cortina: a mitad de camino el proyecto abierto ya es el nuevo, así que para
   * entonces «de dónde vengo» habría dejado de existir en cualquier sitio del
   * que pudiera sacarse. Ver `ProjectSwitchOverlay`.
   */
  const [projectSwitch, setProjectSwitch] = useState<{
    fromName: string
    toName: string
    toId: string
    fromRole?: ProjectAccessRole
    toRole?: ProjectAccessRole
  } | null>(null)
  /**
   * El proyecto al que se llegó CAMBIANDO, para que el Resumen lo diga.
   *
   * Se guarda el identificador y no un simple sí/no porque el sí/no sobrevive a
   * cosas que no debería: al volver al panel y entrar a otro proyecto por la
   * lista, seguiría puesto y ése no se llegó cambiando. Comparándolo con el
   * proyecto abierto, se apaga solo en cuanto deja de ser cierto.
   */
  const [switchedInto, setSwitchedInto] = useState<string | null>(null)
  const [workspaceSection, setWorkspaceSection] = useState<WorkspaceSection>(() => readDashNav().section)
  const [workspaceRecord, setWorkspaceRecord] = useState<string | null>(() => readDashNav().record)
  const { showToast } = useToast()
  const [aiPrompt, setAiPrompt] = useState('')
  /* Abrir el asistente con el borrador guardado en vez de generar uno. */
  const [aiResume, setAiResume] = useState(false)
  const [aiModalOpen, setAiModalOpen] = useState(false)
  /* El asistente desde template: el mismo que el de IA, con el paso de elegir
     el diseño en lugar de la descripción. */
  const [templateFlowOpen, setTemplateFlowOpen] = useState(false)
  const [blankModalOpen, setBlankModalOpen] = useState(false)
  /* El diseño que se armó en la demo pública antes de tener cuenta: al entrar,
     el asistente se abre con él para ponerle nombre y crearlo (ver `demoDesign`).
     Se lee una vez; cerrarlo o crearlo lo olvida. */
  const [demoForm, setDemoForm] = useState(() => readDemoDesign()?.form ?? null)
  /* O lo hecho sobre veltica.com con «Diseña esta página» (`siteDesign`). No
     abre nada por su cuenta: lleva a la pantalla de empezar, y el ESTILO que se
     eligió allí acompaña al camino que se escoja —IA, template o en blanco—.
     Quien además trabajó la página (textos, fotos, secciones) puede seguir con
     ella. Antes se abría siempre el asistente con la portada de Véltica: quien
     sólo había elegido unos colores acababa con un sitio que hablaba de Véltica.
     Se guarda hasta crear el proyecto o descartarlo, así que aguanta una recarga. */
  const [siteDesign, setSiteDesign] = useState(() => peekPendingSiteDesign())
  const startRecipe = siteDesign?.c ? siteRecipe(siteDesign.c) : null
  function dropSiteDesign() {
    clearPendingSiteDesign()
    setSiteDesign(null)
  }
  /** Seguir con la página que se editó en veltica.com: la portada de verdad con todo lo cambiado encima. */
  function continueSitePage() {
    const pending = siteDesign
    if (!pending) return
    void getDemoSite()
      .then((site) => setDemoForm(withSiteDesign(hydrateFromSite(site, t('pages.home')), pending, site.sections)))
      .catch(() => { /* sin la portada no hay qué abrir: se queda en la pantalla de empezar */ })
  }
  function closeDemoFlow() {
    clearDemoDesign()
    setDemoForm(null)
  }
  /** El aviso de «ya tienes todos los proyectos que da tu plan». */
  const [projectLimitOpen, setProjectLimitOpen] = useState(false)
  const { plan: billingPlan, confirmed: planConfirmed } = useBillingPlan()
  // When true the create-with-AI wizard opens in offline mode (bell icon): a static
  // sample to visualize the flow without calling the AI.
  // Brief "is typing" pulse that intensifies the card glow while the user types.
  const aiTextareaRef = useRef<HTMLTextAreaElement>(null)
  const aiTypingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Grow the textarea (and the card with it) to fit the content, up to a max then scroll.
  useEffect(() => {
    const el = aiTextareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`
  }, [aiPrompt])
  useEffect(() => () => { if (aiTypingTimer.current) clearTimeout(aiTypingTimer.current) }, [])
  // Each keystroke nudges each glow blob with its OWN frequency/amplitude (derived from
  // the prompt length), so different regions of the gradient react to typing instead of
  // the whole halo moving as one. Vars ride `transform` with a long springy transition —
  // subtle but alive, and GPU-composited (no repaint glitch).
  const [view, setView] = useState<DashView>(() => readDashNav().view)
  /**
   * Quién tiene cambios sin guardar, en todo el panel.
   *
   * Vive aquí porque aquí es donde se navega —el menú, el atrás del navegador,
   * abrir otro proyecto— y porque el taller cuelga de esta misma pantalla. Cada
   * pantalla que edita algo se apunta sola desde su barra de Guardar.
   */
  const { registry, block: blockUnsaved, hasUnsaved, UnsavedChangesProvider } = useUnsavedRegistry()
  const hasUnsavedRef = useRef(hasUnsaved)
  hasUnsavedRef.current = hasUnsaved
  /* La dirección a la que volver si alguien le da al atrás del navegador con
     algo sin guardar: la de ahora, apuntada mientras todavía estamos aquí. */
  const guardedUrlRef = useRef(window.location.pathname + window.location.search)
  if (!hasUnsaved) guardedUrlRef.current = window.location.pathname + window.location.search
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const goView = useCallback((next: DashView) => {
    if (blockUnsaved()) return
    setView(next)
    setMobileNavOpen(false)
  }, [blockUnsaved])

  /**
   * Ir a una vista del panel desde la pantalla de empezar un proyecto.
   *
   * Esa pantalla se pinta ANTES que cualquier vista (`if (startProjectOpen)`),
   * así que `goView` solo cambiaba la vista por debajo y el menú de usuario
   * parecía roto: Ajustes, Planes, Facturación y Dominios no hacían nada a la
   * vista. Hay que cerrarla primero; y si hay cambios sin guardar, que no se
   * cierre nada.
   */
  const goViewFromStart = useCallback((next: DashView) => {
    if (blockUnsaved()) return
    setStartProjectOpen(false)
    setView(next)
    setMobileNavOpen(false)
  }, [blockUnsaved])

  // Keep browser history in sync with the in-app navigation so back/forward work.
  const restoringNavRef = useRef(false)
  useEffect(() => {
    if (restoringNavRef.current) { restoringNavRef.current = false; return }
    const url = dashNavUrl({ view, projectId: workspaceProjectId, section: workspaceSection, record: workspaceRecord })
    if (url !== window.location.pathname + window.location.search) {
      window.history.pushState(null, '', url)
    }
  }, [view, workspaceProjectId, workspaceSection, workspaceRecord])

  useEffect(() => {
    const onPop = () => {
      /* Con algo sin guardar el atrás del navegador se deshace: se vuelve a
         poner la dirección de esta pantalla y parpadea su barra. */
      if (hasUnsavedRef.current) {
        window.history.pushState(null, '', guardedUrlRef.current)
        blockUnsaved()
        return
      }
      const nav = readDashNav()
      restoringNavRef.current = true
      setView(nav.view)
      setWorkspaceProjectId(nav.projectId)
      setWorkspaceSection(nav.section)
      setWorkspaceRecord(nav.record)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [blockUnsaved])

  useEffect(() => {
    if (!mobileNavOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileNavOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [mobileNavOpen])

  const [filterMenuOpen, setFilterMenuOpen] = useState(false)
  const filterMenuRef = useRef<HTMLDivElement>(null)
  const [templates, setTemplates] = useState<AdminTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templatesError, setTemplatesError] = useState('')
  /* Cuántos hay en total: con más que el tope del servidor, los más viejos no llegan a la lista. */
  const [templatesTotal, setTemplatesTotal] = useState(0)

  useEffect(() => {
    if (!filterMenuOpen) return
    const closeOnPointerDown = (event: PointerEvent) => {
      if (!filterMenuRef.current?.contains(event.target as Node)) setFilterMenuOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFilterMenuOpen(false)
    }
    document.addEventListener('pointerdown', closeOnPointerDown)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnPointerDown)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [filterMenuOpen])
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [templateEditing, setTemplateEditing] = useState<AdminTemplate | null>(null)
  const [templateForm, setTemplateForm] = useState<TemplateForm>(DEFAULT_TEMPLATE_FORM)
  const [templateSaving, setTemplateSaving] = useState(false)
  const [templateThumbUploading, setTemplateThumbUploading] = useState(false)
  const [templateActionId, setTemplateActionId] = useState<string | null>(null)
  const [templateMenuOpen, setTemplateMenuOpen] = useState<string | null>(null)
  /* Filtro y búsqueda de la lista de bloques y plantillas. */
  const [templateFilter, setTemplateFilter] = useState<'all' | 'needsAi' | 'unpublished' | 'drafts'>('all')
  const [templateQuery, setTemplateQuery] = useState('')
  /** La categoría elegida en el carrusel; vacía = todas. */
  const [templateCategory, setTemplateCategory] = useState('')
  /** La parte de la biblioteca de bloques que se mira (ver `templateAreaOf`). */
  const [templateArea, setTemplateArea] = useState<TemplateArea>('sections')
  const [templateAreaTabs, setTemplateAreaTabs] = useState<HTMLDivElement | null>(null)
  useTabThumb(templateAreaTabs, templateArea)
  const [templateSort, setTemplateSort] = useState<TemplateSort>('recent')
  
  const { theme } = useTheme()


  const token = session.access_token
  const [isAdmin, setIsAdmin] = useState(false)
  const [role, setRole] = useState<UserRole>('user')
  const [accessResolved, setAccessResolved] = useState(false)
  const canManageTemplates = role === 'admin' || role === 'designer'
  const email = session.user.email ?? ''
  const meta = session.user.user_metadata as { full_name?: string; name?: string; user_name?: string; avatar_url?: string; picture?: string } | undefined
  const userName = (meta?.full_name || meta?.name || meta?.user_name || (email ? email.split('@')[0] : '') || t('common.user')).trim()
  // Cap the name shown in the top bar so a long name can't stretch the layout.
  const displayName = userName.length > 20 ? `${userName.slice(0, 20).trimEnd()}…` : userName
  const avatarUrl = meta?.avatar_url || meta?.picture || ''

  useEffect(() => {
    let cancelled = false
    void getAccountAccess(token)
      .then((access) => { if (!cancelled) { setIsAdmin(access.isAdmin); setRole(access.role) } })
      .catch(() => { if (!cancelled) { setIsAdmin(false); setRole('user') } })
      .finally(() => { if (!cancelled) setAccessResolved(true) })
    return () => { cancelled = true }
  }, [token])

  useEffect(() => {
    if (!accessResolved) return
    // Admin-only views vs designer-accessible template views.
    /* Facturación no está aquí a propósito: es la cuenta de quien mira, no del
       proyecto, y cada usuario tiene la suya —aunque esté vacía—. Esconderla al
       que no es administrador dejaba a la mayoría sin ver qué se le cobra. */
    /* `projects` entra aquí porque hoy no hay forma de que un usuario llegue a
       ese listado por su cuenta: se empieza creando un sitio con la IA, con una
       plantilla o con un lienzo en blanco, y cualquiera de las tres deja ya un
       proyecto hecho y abierto. Una lista de uno no es una lista.

       Tener varios es lo de agencia, que todavía no existe como producto, así
       que la pantalla se queda para quien la administra. */
    const adminOnlyViews: DashView[] = ['contacts', 'team', 'payments', 'images', 'users', 'aiUnmet', 'adminEmails']
    const templateViews: DashView[] = ['templates', 'siteTemplates']
    if ((adminOnlyViews.includes(view) && role !== 'admin') || (templateViews.includes(view) && !canManageTemplates)) {
      setView('home')
    }
    if (!isAdmin && ADMIN_ONLY_SECTIONS.includes(workspaceSection)) {
      setWorkspaceSection('overview')
    }
  }, [accessResolved, isAdmin, role, canManageTemplates, view, workspaceSection])

  // "Templates" (nav) = full-page website starters; "Bloques" = everything else.
  /* Sin filtrar por texto: el buscador que lo hacía era el de la barra del
     tablero de proyectos, que ya no existe. Las pantallas de plantillas nunca
     tuvieron uno propio, así que el filtro llevaba tiempo sin poder cambiar. */
  // Los de página (`kind: 'page'`) son listas de bloques, no bloques: por ahora se crean fuera del panel.
  const blockTemplates = useMemo(() => templates.filter((t) => t.kind !== 'website' && t.kind !== 'page'), [templates])
  const websiteTemplateList = useMemo(() => templates.filter((t) => t.kind === 'website'), [templates])

  /* Los borrados van aparte de `load()` y no dentro: aquélla es la consulta que
     pinta el panel entero y se reintenta sola cuando la red falla; ésta es un
     añadido que, si falla, sólo debe dejar el selector sin sus fantasmas. */
  const loadDeleted = useCallback(async () => {
    try {
      setDeletedProjects(await listDeletedProjects(token))
    } catch { /* el selector se queda sin ellos y ya */ }
  }, [token])

  useEffect(() => { void loadDeleted() }, [loadDeleted])

  const load = useCallback(async () => {
    try {
      const data = await listProjects(token)
      writeProjectsCache(userId, data)
      setProjects(data)
      listedRef.current = true
      setReconnecting(false)
    } catch {
      // The request already retried once with a refreshed token; if it still failed
      // we're likely offline or the session died. Keep the cached list visible and
      // flag reconnecting so the effect below keeps retrying in the background.
      setReconnecting(true)
    }
    setLoading(false)
  }, [token, userId])

  useEffect(() => { void load() }, [load])

  // A company is the primary application context. On a clean login/root URL,
  // open the last company used (or the first one) instead of stopping at the
  // portfolio-style list of projects.
  useEffect(() => {
    if (loading) return
    if (workspaceProjectId) {
      if (projects.some((project) => project.id === workspaceProjectId)) {
        writeLastActiveProject(userId, workspaceProjectId)
        return
      }
      if (reconnecting) return
      /*
       * Y tampoco mientras la lista siga siendo la copia guardada.
       *
       * `projects` arranca desde `localStorage`, así que en el primer render
       * contiene lo que hubiera la última vez —sin lo que haya llegado desde
       * entonces—. Un proyecto pedido por la dirección y que aún no está en esa
       * copia se leía como «no existe» y acababa expulsado al último proyecto
       * usado, antes de que la consulta real tuviera tiempo de contestar.
       *
       * Pasaba al terminar de recibir un traspaso —el sitio recién recibido no
       * puede estar en una copia anterior a recibirlo— y pasaba igual al aceptar
       * una invitación a un equipo, o al abrir el enlace que alguien te manda a
       * su proyecto. Con la lista de verdad ya en la mano, expulsar sí es
       * correcto: entonces el proyecto de la dirección no existe o no es tuyo.
       */
      if (!listedRef.current) return
      setWorkspaceProjectId(null)
      /* Sin proyectos, a Inicio: es donde están el asistente y las plantillas,
         o sea lo único que se puede hacer sin ninguno. */
      setView('home')
      return
    }
    if (view !== 'home' || projects.length === 0) return
    const lastActive = readLastActiveProject(userId)
    /* Con la lista todavía guardada en el navegador, un proyecto que no está en
       ella no es un proyecto que no existe: puede ser el que se acaba de crear,
       o el que acaban de compartir. Caer al primero aquí no sólo abriría el
       equivocado — lo escribiría como «el último usado» y el bueno se perdería
       para siempre. Se espera a la lista de verdad, que llega enseguida. */
    if (lastActive && !listedRef.current && !projects.some((project) => project.id === lastActive)) return
    const next = projects.find((project) => project.id === lastActive) ?? projects[0]
    if (!next) return
    writeLastActiveProject(userId, next.id)
    setWorkspaceSection('overview')
    setWorkspaceProjectId(next.id)
  }, [isAdmin, loading, projects, reconnecting, userId, view, workspaceProjectId])

  // While reconnecting, keep retrying so the list self-heals once the API/session
  // recovers — no manual refresh needed.
  useEffect(() => {
    if (!reconnecting) return
    const id = window.setInterval(() => { void load() }, 8000)
    return () => window.clearInterval(id)
  }, [reconnecting, load])

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true)
    setTemplatesError('')
    try {
      const listed = await listAdminTemplates(token)
      setTemplates(listed.data)
      setTemplatesTotal(listed.total)
    } catch (error) {
      setTemplatesError(error instanceof Error ? error.message : 'No se pudieron cargar los templates.')
    } finally {
      setTemplatesLoading(false)
    }
  }, [token])

  /* Una vez por entrada a la lista. Antes se volvía a pedir mientras estuviera
     vacía, y una petición que fallaba se repetía sin fin: la ruleta no paraba y
     el error no llegaba a verse. Con un fallo, se ve, y «Actualizar» reintenta. */
  const templatesAsked = useRef(false)
  useEffect(() => {
    const listing = isAdmin && (view === 'templates' || view === 'siteTemplates')
    if (!listing) { templatesAsked.current = false; return }
    if (templatesAsked.current || templates.length > 0 || templatesLoading) return
    templatesAsked.current = true
    void loadTemplates()
  }, [isAdmin, loadTemplates, templates.length, templatesLoading, view])

  function openCreateTemplateModal(kind: TemplateForm['kind'] = 'section') {
    setTemplateEditing(null)
    setTemplateForm({
      ...DEFAULT_TEMPLATE_FORM,
      kind,
      name: kind === 'website' ? 'Nueva plantilla' : DEFAULT_TEMPLATE_FORM.name,
      slug: `${kind === 'website' ? 'plantilla' : DEFAULT_TEMPLATE_FORM.slug}-${Date.now().toString(36)}`,
      category: kind === 'website' ? 'general' : DEFAULT_TEMPLATE_FORM.category,
      isPublic: kind === 'website' ? true : DEFAULT_TEMPLATE_FORM.isPublic,
    })
    setTemplateModalOpen(true)
  }

  function openEditTemplateModal(template: AdminTemplate) {
    setTemplateEditing(template)
    setTemplateForm(templateFormFromTemplate(template))
    setTemplateMenuOpen(null)
    setTemplateModalOpen(true)
  }

  /**
   * Entrar a un proyecto al que acaban de invitarte.
   *
   * Con la misma cortina que cualquier otro cambio de proyecto y no con una
   * recarga: aceptar una invitación ES un cambio de proyecto —el menú, las
   * secciones y las cifras de debajo cambian enteros— y el relevo de nombres es
   * lo que dice a dónde acabas de ir. Una recarga deja la misma pantalla con
   * otro contenido, que es justo lo que la cortina existe para evitar.
   *
   * La lista se refresca ANTES de levantarla: el proyecto recién aceptado no
   * está en ella todavía, y el taller que hay al otro lado lo busca ahí por su
   * nombre. La espera queda tapada por la propia cortina, que para eso está.
   */
  async function enterAcceptedProject(id: string, name: string, role?: ProjectAccessRole) {
    await load()
    if (!workspaceProjectId) { handleOpen(id); return }
    if (projectSwitch) return
    const leaving = projects.find((project) => project.id === workspaceProjectId)
    setProjectSwitch({
      fromName: leaving?.name ?? '',
      toName: name,
      toId: id,
      ...(leaving?.role ? { fromRole: leaving.role } : {}),
      /* El rol que se acaba de aceptar, y no el de la lista: `load()` ya corrió,
         pero `projects` es el estado del render anterior — todavía no tiene el
         proyecto nuevo. */
      ...(role ? { toRole: role } : {}),
    })
  }

  /**
   * La cuenta llegó a cero: ahora sí se borra, y se decide a dónde ir.
   *
   * Al proyecto más reciente que quede, o al asistente si no queda ninguno. Esa
   * elección se hace ANTES de recargar la lista: después de `load()` el borrado
   * ya no está en ella, pero tampoco lo estaría el orden anterior, y hace falta
   * saber cuál era «el siguiente» para que la cortina lo haya anunciado.
   */
  /**
   * La cuenta llegó a cero: se borra. Sólo eso.
   *
   * El cambio de panel NO va aquí: lo pide la cortina a mitad de su relevo, que
   * es donde la escena lo tapa. Hacerlo al volver de la petición dejaría el
   * taller recomponiéndose mientras el renglón todavía dice «Eliminado».
   */
  async function runDelete(target: { id: string; name: string }): Promise<boolean> {
    try {
      const { graceDays } = await deleteProject(token, target.id)
      showToast(t('home.deleted.done', { days: graceDays }))
      return true
    } catch {
      showToast(t('home.deleted.failed'), 'error')
      return false
    }
  }

  /**
   * Recupera un proyecto. Sólo eso.
   *
   * Abrirlo NO va aquí: lo pide la cortina a mitad de su relevo, igual que en el
   * borrado. La lista sí se recarga en cuanto vuelve la petición, porque el
   * proyecto tiene que estar en ella ANTES de que la cortina intente abrirlo.
   */
  async function runRestore(target: DeletedProject): Promise<boolean> {
    try {
      await restoreProject(token, target.id)
      await load()
      await loadDeleted()
      return true
    } catch {
      showToast(t('home.restoreFailed'), 'error')
      return false
    }
  }

  /** El destino tras un borrado: el más reciente que quede, o el asistente. */
  function goAfterDelete(deletedId: string) {
    /* Los proyectos llegan ordenados por `updatedAt` descendente desde el
       servidor, así que «el más reciente que queda» es el primero que no es
       éste. Se calcula con la lista de ANTES de recargar: después el borrado ya
       no está en ella, pero tampoco haría falta buscarlo. */
    const next = projects.find((project) => project.id !== deletedId) ?? null
    if (next) {
      /* El final del borrado ES un cambio de proyecto: pasa por la misma entrada
         que el selector para abrir su Resumen, guardar el nuevo proyecto activo
         y hacer que la mascota confirme «Actualmente estás en…». */
      handleOpen(next.id, true)
    } else {
      setWorkspaceProjectId(null)
      setWorkspaceSection('overview')
      setWorkspaceRecord(null)
      setSwitchedInto(null)
      setView('home')
    }
    void load()
    /* Y la de borrados, que es donde el que se acaba de ir tiene que aparecer.
       Sin esto, `deletedProjects` se quedaba como estaba al abrir el panel —o
       sea, sin él— y su fila fantasma en el selector no salía hasta recargar la
       página entera. El plazo de recuperación corre desde ya, así que la única
       puerta para deshacerlo no puede tardar una recarga en abrirse. */
    void loadDeleted()
  }

  function handleOpen(id: string, viaSwitch = false) {
    writeLastActiveProject(userId, id)
    setWorkspaceSection('overview')
    /* Una ficha del proyecto anterior no pertenece al nuevo. Además de limpiar
       su estado, esto deja la dirección exactamente en el inicio del proyecto. */
    setWorkspaceRecord(null)
    setWorkspaceProjectId(id)
    /* Se apaga en cada entrada y sólo lo enciende el camino del cambio: así
       abrir el mismo proyecto por la lista un rato después no hereda el aviso
       de la vez que se llegó cambiando. */
    setSwitchedInto(viaSwitch ? id : null)
  }


  async function handleTemplateThumbFile(file: File) {
    if (!file.type.startsWith('image/')) { setTemplatesError('Selecciona una imagen para el thumbnail.'); return }
    if (file.size > 5 * 1024 * 1024) { setTemplatesError('La imagen debe pesar menos de 5 MB.'); return }
    setTemplateThumbUploading(true)
    setTemplatesError('')
    try {
      const { url } = await uploadAsset(token, file)
      setTemplateForm((prev) => ({ ...prev, thumbnailUrl: url }))
    } catch (error) {
      setTemplatesError(error instanceof Error ? error.message : 'No se pudo subir la imagen.')
    } finally {
      setTemplateThumbUploading(false)
    }
  }

  async function handleSaveTemplate() {
    const name = templateForm.name.trim()
    const slug = toSlug(templateForm.slug || name)
    const category = templateForm.category.trim()
    if (!name || templateSaving) return
    if (!category) {
      setTemplatesError('Elige una categoría para el template.')
      return
    }
    setTemplateSaving(true)
    setTemplatesError('')
    // The taxonomy facets are the single tagging surface. styleTags/aiTags are
    // derived so the search index and the project generator keep working.
    // `media` y `columns` se deducen del contenido al armar el catálogo de la
    // IA: si se guardaran aquí, un valor viejo taparía lo que hay en el canvas.
    const { media: _media, columns: _columns, ...chosenFacets } = templateForm.facets
    // Un sitio entero se clasifica por industria: va en `bestFor`, no en la
    // faceta `category`, que sólo admite lo que es un bloque.
    const isSite = templateForm.kind === 'website'
    const facets: TemplateForm['facets'] = isSite ? { bestFor: [category] } : { ...chosenFacets, category }
    const aiSection = isSite ? undefined : CATEGORY_TO_AI_SECTION[category]
    const payload = {
      name,
      slug,
      kind: templateForm.kind,
      category,
      description: templateForm.description.trim(),
      facets,
      styleTags: facets.style ?? [],
      colorTags: facets.colorScheme ?? [],
      aiTags: aiSection ? [aiSection] : [],
      isPremium: templateForm.isPremium,
      thumbnailUrl: templateForm.thumbnailUrl.trim() || null,
      ...(!templateEditing ? { isPublic: templateForm.isPublic } : {}),
    }
    try {
      const saved = templateEditing
        ? await updateAdminTemplateMeta(token, templateEditing.id, payload)
        : await createAdminTemplate(token, payload)
      setTemplates((prev) => {
        if (!templateEditing) return [saved, ...prev]
        return prev.map((item) => (item.id === saved.id ? saved : item))
      })
      setTemplateModalOpen(false)
      setTemplateEditing(null)
    } catch (error) {
      setTemplatesError(error instanceof Error ? error.message : 'No se pudo guardar el template.')
    } finally {
      setTemplateSaving(false)
    }
  }

  async function runTemplateAction(template: AdminTemplate, action: 'publish' | 'archive' | 'activate' | 'reset' | 'delete' | 'aiOn' | 'aiOff') {
    setTemplateMenuOpen(null)
    if (action === 'delete' && !confirm(t('home.template.confirmDelete', { name: template.name }))) return
    if (action === 'reset' && !confirm(t('home.template.confirmReset', { name: template.name }))) return
    setTemplateActionId(template.id)
    setTemplatesError('')
    try {
      if (action === 'delete') {
        await deleteAdminTemplate(token, template.id)
        setTemplates((prev) => prev.filter((item) => item.id !== template.id))
        return
      }
      /* Si la IA lo usa: sólo cambia la marca `manualOnly` de sus facetas (la
         ruta vacía el catálogo de la IA, así que llega al momento). */
      if (action === 'aiOn' || action === 'aiOff') {
        const { manualOnly: _drop, ...rest } = template.facets
        await updateAdminTemplateMeta(token, template.id, { facets: action === 'aiOff' ? { ...rest, manualOnly: true } : rest })
      } else if (action === 'publish') await publishAdminTemplate(token, template.id)
      else if (action === 'archive') await archiveAdminTemplate(token, template.id)
      else if (action === 'activate') await activateAdminTemplate(token, template.id)
      else await resetAdminTemplateDraft(token, template.id)
      // Reload so status + "unpublished changes" flags reflect the new state.
      await loadTemplates()
    } catch (error) {
      setTemplatesError(error instanceof Error ? error.message : 'No se pudo actualizar el template.')
    } finally {
      setTemplateActionId(null)
    }
  }

  function renderTemplateManager(opts: {
    list: AdminTemplate[]
    title: string
    subtitle: string
    newKind: TemplateForm['kind']
    emptyTitle: string
    emptyDesc: string
    showThumb?: boolean
  }) {
    const isBlocks = opts.newKind !== 'website'
    const matches = {
      all: () => true,
      needsAi: (template: AdminTemplate) => Boolean(template.aiReadiness && !template.aiReadiness.ready),
      unpublished: (template: AdminTemplate) => Boolean(template.hasUnpublishedChanges),
      drafts: (template: AdminTemplate) => template.status === 'draft',
    } as const
    const filters = (isBlocks ? ['all', 'needsAi', 'unpublished', 'drafts'] : ['all', 'unpublished', 'drafts']) as (keyof typeof matches)[]
    const activeFilter = filters.includes(templateFilter) ? templateFilter : 'all'
    const query = templateQuery.trim().toLowerCase()
    /* Un bloque se agrupa por lo que ES (hero, precios…); un sitio entero, por
       la industria a la que va. En el orden de la taxonomía, y lo que no esté en
       ella —una categoría vieja escrita a mano— al final. */
    const facet = isBlocks ? 'category' : 'bestFor'
    const categoryOf = (template: AdminTemplate) => (isBlocks ? template.facets.category : undefined) ?? template.category ?? ''
    const categoryLabel = (value: string) => {
      const key = taxonomyLabelKey(facet, value)
      return key === value ? value : t(key)
    }
    /* En los bloques, una parte cada vez: secciones, catálogo, blog, CMS o
       componentes. Lo demás —filtros, búsqueda, categorías— mira sólo esa. */
    const areaCount = (area: TemplateArea) => opts.list.filter((template) => templateAreaOf(template) === area).length
    const list = isBlocks ? opts.list.filter((template) => templateAreaOf(template) === templateArea) : opts.list
    const grouped = isBlocks && (templateArea === 'blog' || templateArea === 'cms')
    const filtered = list
      .filter(matches[activeFilter])
      .filter((template) => !query || matchesSearch(template.name, query) || matchesSearch(template.description, query))
    const order = (isBlocks ? TEMPLATE_CATEGORIES : TEMPLATE_INDUSTRIES).map((option) => option.value)
    const present = grouped ? [] : [...new Set(list.map(categoryOf).filter(Boolean))]
      .sort((a, b) => (order.indexOf(a) + 1 || 999) - (order.indexOf(b) + 1 || 999) || a.localeCompare(b))
    const activeCategory = present.includes(templateCategory) ? templateCategory : ''
    const visible = sortTemplates(
      filtered.filter((template) => !activeCategory || categoryOf(template) === activeCategory),
      templateSort,
    )
    const renderCard = (template: AdminTemplate) => (
      <TemplateAdminCard
        key={template.id}
        template={template}
        menuOpen={templateMenuOpen === template.id}
        actionPending={templateActionId === template.id}
        showThumbnail={Boolean(opts.showThumb)}
        onMenuOpenChange={(open) => setTemplateMenuOpen(open ? template.id : null)}
        onOpenCanvas={() => { window.location.href = editorHref(`?templateId=${template.id}`) }}
        onEdit={() => openEditTemplateModal(template)}
        onAction={(action) => { void runTemplateAction(template, action) }}
      />
    )
    const filterLabel = {
      all: 'templateList.filterAll',
      needsAi: 'templateList.filterNeedsAi',
      unpublished: 'templateList.filterUnpublished',
      drafts: 'templateList.filterDrafts',
    } as const
    return (
      <section className="dash-template-admin dash-standard-page">
        <DashboardPageHeader
          title={opts.title}
          subtitle={opts.subtitle}
          actions={
            <>
              <button className="dash-secondary-btn" type="button" onClick={() => { void loadTemplates() }} disabled={templatesLoading}>
                {templatesLoading ? <IconLoader2 className="dash-spin" size={14} /> : null}
                {t('create.refreshTemplates')}
              </button>
              <button className="dash-primary-btn" type="button" onClick={() => openCreateTemplateModal(opts.newKind)}>
                <IconPlus size={15} />
                {t(opts.newKind === 'website' ? 'templateList.newSite' : 'templateList.newBlock')}
              </button>
            </>
          }
        />
        {templatesError ? <p className="dash-ai-error">{templatesError}</p> : null}
        {/* Al tope: la lista trae los más recientes y el resto no se ve. Avisado
            para que nadie dé por perdido un bloque que sigue ahí; el día que
            pase, toca paginar con los filtros en el servidor. */}
        {templatesTotal > templates.length ? (
          <p className="dash-ai-error" role="status">{t('templateList.truncated', { shown: templates.length, total: templatesTotal })}</p>
        ) : null}
        {/* El camino de un bloque hasta la IA, a la vista: quien diseña sabe
            qué se espera de él sin tener que preguntarlo. */}
        {isBlocks ? (
          <section className="dash-template-guide" aria-label={t('templateList.howTitle')}>
            <strong>{t('templateList.howTitle')}</strong>
            <ol>
              <li><span>1</span>{t('templateList.step1')}</li>
              <li><span>2</span>{t('templateList.step2')}</li>
              <li><span>3</span>{t('templateList.step3')}</li>
            </ol>
          </section>
        ) : null}
        {isBlocks && opts.list.length > 0 ? (
          <div className="dash-template-areas">
            <div className="dash-booking-service-tabs" ref={setTemplateAreaTabs} role="tablist" aria-label={t('templateList.areas')}>
              <span className="dash-booking-service-tabs-thumb" aria-hidden="true" />
              {TEMPLATE_AREAS.map((area) => (
                <button
                  key={area}
                  type="button"
                  role="tab"
                  aria-selected={templateArea === area}
                  className={templateArea === area ? 'is-active' : ''}
                  onClick={() => { setTemplateArea(area); setTemplateCategory('') }}
                >
                  <span>{t(`templateList.area.${area}`)}</span>
                  <span className="dash-cp-tab-count">{areaCount(area)}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {opts.list.length > 0 ? (
          <div className="dash-template-toolbar">
            <div className="dash-chip-group" role="tablist">
              {filters.map((filter) => {
                const count = list.filter(matches[filter]).length
                return (
                  <button
                    key={filter}
                    type="button"
                    role="tab"
                    aria-selected={activeFilter === filter}
                    className={`dash-chip${activeFilter === filter ? ' active' : ''}${filter === 'needsAi' && count > 0 ? ' is-warning' : ''}`}
                    onClick={() => setTemplateFilter(filter)}
                  >
                    {t(filterLabel[filter])}
                    <b>{count}</b>
                  </button>
                )
              })}
            </div>
            <div className="dash-template-toolbar-end">
              <label className="dash-template-sort">
                <span>{t('templateList.sort')}</span>
                <select value={templateSort} onChange={(event) => setTemplateSort(event.target.value as TemplateSort)}>
                  {TEMPLATE_SORTS.map((sort) => <option key={sort} value={sort}>{t(`templateList.sort.${sort}`)}</option>)}
                </select>
              </label>
              <input
                className="dash-template-search"
                type="search"
                value={templateQuery}
                placeholder={t('templateList.search')}
                aria-label={t('templateList.search')}
                onChange={(event) => setTemplateQuery(event.target.value)}
              />
            </div>
          </div>
        ) : null}
        {/* Las categorías en un carrusel: una fila que se desliza, con cuántas
            hay de cada una según el filtro y la búsqueda de arriba. */}
        {present.length > 1 ? (
          <div className="dash-template-categories" role="tablist" aria-label={t(isBlocks ? 'templateModal.category' : 'templateModal.industry')}>
            {['', ...present].map((value) => {
              const count = value ? filtered.filter((template) => categoryOf(template) === value).length : filtered.length
              return (
                <button
                  key={value || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={activeCategory === value}
                  className={`dash-chip${activeCategory === value ? ' active' : ''}`}
                  disabled={count === 0 && activeCategory !== value}
                  onClick={() => setTemplateCategory(value)}
                >
                  {value ? categoryLabel(value) : t('filter.all')}
                  <b>{count}</b>
                </button>
              )
            })}
          </div>
        ) : null}
        {templatesLoading ? (
          <div className="dash-loading"><IconLoader2 className="dash-spin" size={24} /></div>
        ) : opts.list.length === 0 ? (
          <section className="dash-placeholder">
            <IconLayoutBoard size={34} aria-hidden="true" />
            <strong>{opts.emptyTitle}</strong>
            <span>{opts.emptyDesc}</span>
            <button className="dash-primary-btn" type="button" onClick={() => openCreateTemplateModal(opts.newKind)}>
              <IconPlus size={15} />
              {t(opts.newKind === 'website' ? 'templateList.createSite' : 'templateList.createBlock')}
            </button>
          </section>
        ) : visible.length === 0 ? (
          <p className="dash-template-empty-filter">{t('templateList.empty')}</p>
        ) : grouped ? (
          /* Blog y CMS: las secciones sueltas y luego cada diseño de página con
             sus bloques dentro. Un bloque de un diseño no se inserta solo, así
             que suelto entre los demás no se sabía de qué era. */
          <div className="dash-template-groups">
            {templateGroups(visible).map((group) => (
              <section key={group.id} className="dash-template-group">
                <h3 className="dash-template-group-head">
                  {group.name ?? t('templateList.groupSections')}
                  {group.category ? <span>{t(`templateList.design.${group.category}`)}</span> : null}
                </h3>
                <div className="dash-template-grid">{group.items.map((template) => renderCard(template))}</div>
              </section>
            ))}
          </div>
        ) : (
          <div className="dash-template-grid">
            {visible.map((template) => renderCard(template))}
          </div>
        )}
      </section>
    )
  }

  const workspaceGlobalView = WORKSPACE_GLOBAL_VIEWS.includes(view as WorkspaceGlobalView)
    ? view as WorkspaceGlobalView
    : null

  /**
   * El listado de empresas, en una variable porque se pinta en dos marcos.
   *
   * Dentro del workspace es una vista global más —como Usuarios o Plantillas—
   * y conserva el menú lateral completo; fuera, es la pantalla suelta de
   * siempre. Es el mismo listado: tenerlo escrito una vez es lo que impide
   * que las dos copias empiecen a separarse.
   */
  const workspaceGlobalContent = workspaceGlobalView === 'plans' ? (
    /* Los planes, alojados por el taller como cualquier otra vista de cuenta:
       se está en un proyecto y se va a mirar LA MEMBRESÍA, que no es de él. */
    <ProjectPlans token={token} onOpenBilling={() => goView('billing')} />
  ) : workspaceGlobalView === 'usage' ? (
    <AccountUsage token={token} projects={projects} onOpenPlans={() => { if (!blockUnsaved()) goView('plans') }} />
  ) : workspaceGlobalView === 'settings' ? (
    /* Con «Región» viva: aquí SÍ hay un proyecto abierto detrás, así que la
       frase del idioma puede llevar a la Región de ese proyecto. En la pantalla
       suelta de más abajo no lo hay y se queda en texto. */
    <WorkspaceSettings
      session={session}
      projects={projects}
      onOpenRegion={() => {
        if (blockUnsaved()) return
        setView('home')
        setWorkspaceSection('region')
      }}
    />
  ) : workspaceGlobalView === 'domains' ? (
    <AccountDomains
      token={token}
      onOpenProject={(id) => {
        if (blockUnsaved()) return
        handleOpen(id)
        setWorkspaceSection('publish')
      }}
    />
  ) : workspaceGlobalView === 'billing' ? (
    /* Los planes ya no cuelgan del proyecto abierto, así que «cambiar de plan»
       se puede ofrecer siempre — antes esta misma pantalla, vista sin ningún
       proyecto, se quedaba sin ese botón porque no había sección a la que ir. */
    <BillingLedger
      token={token}
      onOpenPlans={() => {
        if (blockUnsaved()) return
        goView('plans')
      }}
      onOpenDomains={() => {
        if (blockUnsaved()) return
        setView('home')
        setWorkspaceSection('publish')
      }}
      /* Un dominio cobrado se administra donde se conectó, no aquí: la fila
         lleva a los ajustes de publicación de su propio proyecto. */
      onOpenProject={(id) => {
        if (blockUnsaved()) return
        setView('home')
        handleOpen(id)
        setWorkspaceSection('publish')
      }}
    />
  ) : isAdmin && workspaceGlobalView === 'users' ? (
    <AdminUsers token={token} />
  ) : isAdmin && workspaceGlobalView === 'authCodes' ? (
    <AdminAuthCodes token={token} />
  ) : isAdmin && workspaceGlobalView === 'aiUnmet' ? (
    <AdminAiUnmet token={token} />
  ) : isAdmin && workspaceGlobalView === 'adminEmails' ? (
    <AdminEmails token={token} />
  ) : isAdmin && workspaceGlobalView === 'images' ? (
    <AdminImages token={token} />
  ) : workspaceGlobalView === 'siteTemplates' ? renderTemplateManager({
    list: websiteTemplateList,
    title: t('templateList.sitesTitle'),
    subtitle: t('templateList.sitesSubtitle'),
    newKind: 'website',
    emptyTitle: t('templateList.sitesEmptyTitle'),
    emptyDesc: t('templateList.sitesEmptyDesc'),
    showThumb: true,
  }) : workspaceGlobalView === 'templates' ? renderTemplateManager({
    list: blockTemplates,
    title: t('templateList.blocksTitle'),
    subtitle: t('templateList.blocksSubtitle'),
    newKind: 'section',
    emptyTitle: t('templateList.blocksEmptyTitle'),
    emptyDesc: t('templateList.blocksEmptyDesc'),
    // Con miniatura, igual que las plantillas de sitio: es la captura que hace
    // el servidor al publicar, así que a simple vista se ve cuáles ya la tienen
    // —las que salen con el dibujo de bloque no se han publicado desde que
    // existe la captura, y son las que el catálogo no puede enseñar—.
    showThumb: true,
  }) : null

  const templateDataModal = (
    <TemplateDataModal
      token={token}
      open={templateModalOpen}
      editing={templateEditing}
      form={templateForm}
      setForm={setTemplateForm}
      saving={templateSaving}
      thumbnailUploading={templateThumbUploading}
      error={templatesError}
      onClose={() => setTemplateModalOpen(false)}
      onThumbnailFile={(file) => { void handleTemplateThumbFile(file) }}
      onSave={() => { void handleSaveTemplate() }}
    />
  )

  /**
   * Si la membresía ya no da para otro proyecto.
   *
   * Cuenta sólo los PROPIOS: los compartidos los paga quien los creó, y
   * descontárselos a quien entra invitado le llenaría el cupo con trabajo ajeno.
   *
   * Mientras el plan no esté confirmado no se bloquea nada. El valor de arranque
   * es una caché que por defecto dice «gratis», así que bloquear con él sería
   * enseñarle un cartel de mejorar plan a alguien que ya paga. El tope de verdad
   * lo aplica el servidor —`enforceProjectLimit`—, y su 402 se recoge más abajo:
   * en esa ventana de un segundo el aviso llega igual, sólo que un paso después.
   */
  function projectLimitReached(): boolean {
    if (!planConfirmed || !accessResolved) return false
    const owned = projects.filter((project) => !project.shared).length
    /* Un admin tiene su propio cupo, tenga el plan que tenga. */
    if (!reachedLimit(owned, isAdmin ? ADMIN_PROJECT_LIMIT : PROJECT_LIMITS[billingPlan])) return false
    setProjectLimitOpen(true)
    return true
  }

  /**
   * Por dónde se empieza un proyecto: IA, plantilla o en blanco.
   *
   * Una sola función porque hay DOS sitios que ofrecen los tres caminos: la
   * pantalla de quien no tiene ninguno y el elegidor que se abre desde el menú
   * de un proyecto. Con el reparto escrito en cada uno, el día que se añada un
   * cuarto camino habría que acordarse de los dos.
   */
  /* Lo que se escribió en la caja de IA de veltica.com antes de tener cuenta:
     al entrar, el asistente se abre con ello y genera (ver `pendingPrompt`).
     El negocio y lo que quiere, si los eligió, cuentan como su bienvenida. */
  /* El texto se le pasa a la pantalla de empezar (`startPrompt`), que es la que
     pregunta primero la industria y lo que se quiere del sitio a quien es
     nuevo y sólo después genera: aquí sólo se abre esa pantalla. */
  const [pendingPrompt, setPendingPrompt] = useState(() => {
    const pending = consumePendingPrompt()
    /* Aquí y no en un efecto: la pantalla de empezar decide al montarse si
       pregunta la bienvenida, y un efecto llega después de ese primer dibujo
       —quien ya eligió su negocio en veltica.com veía la pregunta otra vez—. */
    if (pending?.brief && !welcomeDone(userId)) saveWelcome(userId, pending.brief)
    return pending
  })
  useEffect(() => {
    if (!pendingPrompt) return
    if (pendingPrompt.brief && !welcomeDone(userId)) saveWelcome(userId, pendingPrompt.brief)
    setStartProjectOpen(true)
    // Una vez, al entrar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt])
  /* Y lo mismo con un diseño traído de veltica.com: a elegir por dónde empezar.
     Una vez, al llegar: lo guardado dura hasta crear el proyecto, y abrir esta
     pantalla en cada entrada al panel mientras tanto obligaba a cerrarla
     siempre a quien se arrepintió. */
  useEffect(() => {
    if (siteDesign && takePendingSiteDesignArrival()) setStartProjectOpen(true)
    // Una vez, al entrar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const carriedExample = siteExample(siteDesign)
  const carried = siteDesign ? {
    label: siteDesignLabel(siteDesign),
    hasContent: siteDesignHasContent(siteDesign),
    ...(carriedExample ? { example: { name: carriedExample.name, prompt: carriedExample.prompt, business: carriedExample.business, goals: carriedExample.goals } } : {}),
  } : undefined

  function startProject(option: 'ai' | 'resume' | 'template' | 'blank' | 'site', prompt = '') {
    /* El cupo se mira ANTES de abrir ningún camino: dejar escribir la
       descripción, elegir plantilla y colores para chocar con el tope al final
       es trabajo tirado. */
    if (projectLimitReached()) return
    /* La pantalla de empezar NO se cierra: se queda debajo. Los tres caminos se
       abren encima, y su X los cierra a ellos — quien se arrepiente de elegir IA
       quería volver a elegir, no salirse del todo. Cerrándola aquí, la X
       devolvía al panel y había que empezar de nuevo desde el menú.

       Cuando el camino termina de verdad, la página navega al editor del
       proyecto nuevo, así que lo que quede debajo da igual. */
    if (option === 'site') { continueSitePage(); return }
    if (option === 'ai') { setAiResume(false); setAiPrompt(prompt); setAiModalOpen(true); return }
    if (option === 'resume') { setAiResume(true); setAiPrompt(''); setAiModalOpen(true); return }
    /* En blanco tiene su propia pantalla: un campo y a editar. Antes abría el
       catálogo de plantillas con «en blanco» ya elegido, o sea que para NO
       elegir plantilla había que entrar a un catálogo de plantillas. */
    if (option === 'blank') { setBlankModalOpen(true); return }
    setTemplateFlowOpen(true)
  }

  /**
   * Los dos caminos que tienen pantalla propia: IA y en blanco.
   *
   * En una variable por lo mismo que el asistente de plantillas: estaban
   * escritos sólo en la rama de «no tengo proyectos», así que al elegir IA o en
   * blanco desde dentro de un proyecto el estado se ponía en cierto y no había
   * nada montado que lo mirara — se cerraba el elegidor y no pasaba nada más.
   */
  /**
   * Salir de la creación entera, al panel.
   *
   * Cierra el camino que esté abierto Y la pantalla de empezar que hay debajo:
   * es la salida larga, la que ofrece la pastilla de la barra. La X de cada
   * asistente hace la corta —vuelve a elegir camino— y por eso son dos.
   */
  function exitStartProject() {
    setAiModalOpen(false)
    setTemplateFlowOpen(false)
    setBlankModalOpen(false)
    setStartProjectOpen(false)
    if (demoForm) closeDemoFlow()
    dropSiteDesign()
  }

  /**
   * Lo que hace un camino a pantalla completa cuando el servidor le dice que no
   * cabe otro proyecto: se sale de él y se enseña el aviso en el panel.
   *
   * En el panel y no dentro del asistente porque desde ahí se ofrece mejorar el
   * plan, y esa pantalla vive aquí: enseñarlo encima de un asistente que hay que
   * cerrar para poder actuar sólo añade un paso.
   */
  function showProjectLimit() {
    exitStartProject()
    setProjectLimitOpen(true)
  }

  const startProjectFlows = (
    <>
      <CreateWithAiModal
        open={aiModalOpen}
        token={token}
        initialPrompt={aiPrompt}
        userId={userId}
        resume={aiResume}
        startRecipe={startRecipe}
        onClose={() => setAiModalOpen(false)}
        onExit={exitStartProject}
        onPlanLimit={showProjectLimit}
        onCreated={(id) => { clearPendingSiteDesign(); writeLastActiveProject(userId, id) }}
      />
      <CreateWithAiModal
        open={templateFlowOpen}
        mode="template"
        token={token}
        startRecipe={startRecipe}
        onClose={() => setTemplateFlowOpen(false)}
        onExit={exitStartProject}
        onPlanLimit={showProjectLimit}
        onCreated={(id) => { clearPendingSiteDesign(); writeLastActiveProject(userId, id) }}
      />
      <CreateWithAiModal
        open={demoForm !== null}
        mode="demo"
        initialForm={demoForm}
        token={token}
        onClose={closeDemoFlow}
        onExit={exitStartProject}
        onPlanLimit={showProjectLimit}
        onCreated={(id) => { clearDemoDesign(); clearPendingSiteDesign(); writeLastActiveProject(userId, id) }}
      />
      <CreateBlankModal
        open={blankModalOpen}
        token={token}
        startRecipe={startRecipe}
        onCreated={clearPendingSiteDesign}
        onClose={() => setBlankModalOpen(false)}
        onExit={exitStartProject}
        onPlanLimit={showProjectLimit}
      />
    </>
  )


  /**
   * El asistente de proyecto nuevo, en una variable.
   *
   * Estaba escrito dentro de la pantalla sin proyectos, y ahí sólo existía
   * cuando no había ninguno abierto. Desde que se puede crear desde el menú de
   * un proyecto hacía falta en los dos sitios: al salir del workspace para
   * enseñarlo, el efecto que reabre el último proyecto volvía a entrar y el
   * asistente no llegaba a montarse nunca — se veía como que la pantalla se
   * reiniciaba sola.
   */
  /**
   * El aviso de cupo lleno, en una variable por lo mismo que el asistente: hay
   * tres pantallas que pueden estar debajo cuando alguien pide un proyecto
   * nuevo —el panel, el taller de un proyecto y la de quien no tiene ninguno— y
   * el aviso tiene que salir en las tres.
   */
  const projectLimitModal = projectLimitOpen ? (
    <PlanLimitModal
      resource="proyectos"
      currentPlan={billingPlan}
      onClose={() => setProjectLimitOpen(false)}
      onOpenPlans={() => goView('plans')}
    />
  ) : null

  /**
   * Empezar un proyecto: la pantalla entera, no una ventana encima.
   *
   * Es la MISMA de quien no tiene ninguno —mismo marco, misma barra, mismas tres
   * tarjetas—, y por eso se sirve igual: ocupando la pantalla. Metida en una
   * ventana flotante habría dos formas distintas de ver la misma decisión según
   * por dónde se llegue, y la de la ventana además apretaba las tres tarjetas
   * contra un ancho que no es el suyo.
   *
   * El proyecto abierto NO se cierra: esto se pinta antes que él y se quita al
   * volver, así que cancelar devuelve exactamente a donde se estaba. Cerrarlo de
   * verdad tendría el efecto de reabrir el último peleándose por volver a
   * entrar, que es lo que hacía que la pantalla pareciera reiniciarse.
   */
  if (startProjectOpen) {
    return (
      <UnsavedChangesProvider value={registry}>
      <div className="dash-shell dash-shell--topbar dash-shell--solo">
        <DashboardTopbar
          mobileNavOpen={mobileNavOpen}
          onToggleMobileNav={() => setMobileNavOpen((open) => !open)}
          token={token}
          /* «Regresar» sólo si hay a dónde: con una cuenta recién creada no
             hay proyectos ni panel detrás —esta pantalla ES el principio—, y
             la pastilla llevaba a la misma pantalla. Ahí va la marca sola
             (mascota, logotipo y Beta), sin hacer nada al pulsarla. */
          /* Volver sin crear nada es arrepentirse: lo traído de veltica.com se
             suelta, para que no se cuele luego en otro proyecto sin avisar. */
          {...(projects.length > 0 ? { onHome: () => { setStartProjectOpen(false); dropSiteDesign() }, backToDashboard: true } : {})}
          {...(workspaceProjectId ? { projectId: workspaceProjectId } : {})}
        />
        <DashboardTopbarEnd>
          <DashboardUserMenu
            avatarUrl={avatarUrl}
            userName={userName}
            displayName={displayName}
            onOpenSettings={() => goViewFromStart('settings')}
            onOpenPlans={() => goViewFromStart('plans')}
            onOpenUsage={() => goViewFromStart('usage')}
            onOpenBilling={() => goViewFromStart('billing')}
            onOpenDomains={() => goViewFromStart('domains')}
          />
        </DashboardTopbarEnd>

        {mobileNavOpen && workspaceProjectId ? (
          <>
            <div className="dash-sidebar-backdrop is-open" role="presentation" onClick={() => setMobileNavOpen(false)} />
            <WorkspaceSidebar
              project={{
                currentProjectId: workspaceProjectId,
                currentProjectName: projects.find((project) => project.id === workspaceProjectId)?.name ?? '',
                loading,
                projects,
                onSelectProject: (id) => { setStartProjectOpen(false); handleOpen(id) },
                onCreateProject: () => setMobileNavOpen(false),
              }}
              active={workspaceSection === 'bookingsList' ? 'bookingsCalendar' : workspaceSection}
              onSelect={(section) => {
                setStartProjectOpen(false)
                setView('home')
                setWorkspaceSection(section)
                setWorkspaceRecord(null)
                setMobileNavOpen(false)
              }}
              isAdmin={isAdmin}
              role={role}
              projectRole={projects.find((project) => project.id === workspaceProjectId)?.role ?? 'owner'}
              onGlobalNavigate={goViewFromStart}
              avatarUrl={avatarUrl}
              userName={userName}
              userId={userId}
              displayName={displayName}
              open={mobileNavOpen}
              onClose={() => setMobileNavOpen(false)}
              onHome={() => setStartProjectOpen(false)}
            />
          </>
        ) : null}

        <main className="dash-main">
          <div className="dash-workspace-content">
            {/* `justDeleted` en falso: aquí se llega teniendo proyectos, y el
                encabezado de «te quedaste sin sitios» sería mentira. */}
            {/* Antes de proponerle crear un sitio: puede que ya le hayan
                invitado a uno y sólo le falte aceptar. */}
            <TeamInviteBanner token={token} onAccepted={(id, name, role) => { void enterAcceptedProject(id, name, role) }} />
            {/* Y los traspasos, con el de invitaciones: los dos son avisos que
                caducan y piden una respuesta. */}
            <TransferBanner token={token} />
            <StartProjectScreen
              onStart={startProject}
              onOpenPlans={() => goViewFromStart('plans')}
              userId={userId}
              token={token}
              flowOpen={aiModalOpen}
              startPrompt={pendingPrompt?.prompt}
              onStartPromptUsed={() => setPendingPrompt(null)}
              carried={carried}
              onDropCarried={dropSiteDesign}
            />
            {/* Los borrados, DEBAJO de las formas de empezar: hay treinta días
                para recuperarlos, así que no van por delante de aquello a lo que
                se viene. Aquí y no en el taller porque esta pantalla no tiene
                menú lateral, que es donde viven el resto del tiempo. */}
            <DeletedProjectsBanner token={token} onRestored={() => window.location.reload()} />
          </div>
        </main>
      </div>
      {projectLimitModal}
      {startProjectFlows}
      </UnsavedChangesProvider>
    )
  }


  if (workspaceProjectId) {
    return (
      <UnsavedChangesProvider value={registry}>
      <Workspace
        projectId={workspaceProjectId}
        token={token}
        section={workspaceSection}
        onSectionChange={(next) => {
          if (blockUnsaved()) return
          setView('home')
          setWorkspaceSection(next)
          /* El aviso de «acabas de cambiar de proyecto» dura hasta que se
             empieza a trabajar. Moverse por el menú ya es haberlo leído, y
             volver al Resumen media hora después para encontrarse a la mascota
             anunciando un cambio de hace media hora es una noticia caducada. */
          setSwitchedInto(null)
          // Cambiar de sección cierra la ficha abierta: su id no significa nada
          // en la sección de al lado.
          setWorkspaceRecord(null)
        }}
        record={workspaceRecord}
        onRecordChange={setWorkspaceRecord}
        onBack={() => {
          if (blockUnsaved()) return
          setWorkspaceProjectId(null)
          setView('home')
          void load()
        }}
        /* Crear desde dentro de un proyecto: se sale al panel y se abre el
           asistente, que vive aquí. La guarda de cambios sin guardar la pone el
           workspace antes de llamar. */
        /* Sin salir del proyecto: el asistente se monta ENCIMA. Salir no valía —
           el efecto que reabre el último proyecto volvía a entrar antes de que
           el asistente llegara a pintarse, y se veía como que la pantalla se
           reiniciaba sola. */
        onCreateProject={() => { setStartProjectOpen(true) }}
        /* Confirmado en Configuración, pero aún sin borrar: se levanta la
           cortina con su cuenta atrás y es ella quien dispara la petición al
           llegar a cero. Ver `ProjectDeleteOverlay`. */
        onProjectDeleted={() => {
          const target = projects.find((project) => project.id === workspaceProjectId)
          if (!target) return
          setDeleting({ id: target.id, name: target.name })
        }}
        onOpenEditor={(pageId?: string, options?: { newPage?: boolean; idea?: string }) => { window.location.href = editorHref(`?pid=${workspaceProjectId}${pageId ? `&page=${pageId}` : ''}${options?.newPage ? '&newPage=1' : ''}${options?.newPage && options.idea ? `&newPageIdea=${encodeURIComponent(options.idea)}` : ''}`) }}
        userId={userId}
        avatarUrl={avatarUrl}
        userName={userName}
        displayName={displayName}
        isAdmin={isAdmin}
        role={role}
        globalView={workspaceGlobalView}
        globalContent={workspaceGlobalContent}
        onGlobalNavigate={goView}
        onHome={() => {
          if (blockUnsaved()) return
          setView('home')
          setWorkspaceSection('overview')
        }}
        /* Con el ROL, no sólo id y nombre. El recorte venía de cuando el taller
           sólo necesitaba pintar la lista del selector; desde que hay equipo, el
           rol decide el menú, la pastilla del usuario y la corona de la tarjeta,
           y quedándose fuera aquí todo eso caía al `?? 'owner'` de más abajo: un
           invitado veía «PRO» y el menú entero. */
        /* Con su favicon: es la cara de cada proyecto en el selector. */
        projects={projects.map(({ id, name, role, faviconLight, faviconDark }) => ({
          id,
          name,
          ...(role ? { role } : {}),
          ...(faviconLight ? { faviconLight } : {}),
          ...(faviconDark ? { faviconDark } : {}),
        }))}
        arrivedBySwitch={switchedInto === workspaceProjectId}
        onEnterAcceptedProject={(id, name, role) => { void enterAcceptedProject(id, name, role) }}
        deletedProjects={deletedProjects}
        /* Se busca en la lista completa en vez de usar lo que llega: el selector
           sólo necesita id, nombre y plazo, y el diálogo quiere la fila entera. */
        onOpenDeleted={(project) => {
          setReviewingDeleted(deletedProjects.find((row) => row.id === project.id) ?? null)
        }}
        onProjectSelect={(id) => {
          if (blockUnsaved()) return
          /* Con una cortina puesta no se admite otra: el segundo clic mataría
             el guion del primero a mitad, y el panel se quedaría enseñando el
             proyecto de en medio. */
          if (projectSwitch) return
          const target = projects.find((project) => project.id === id)
          /* Sin proyecto del que salir no hay nada que relevar —se está
             entrando, no cambiando— y la cortina sólo sería una espera de más
             delante de una pantalla que ya iba a pintarse. */
          if (!target || id === workspaceProjectId) { handleOpen(id); return }
          const leaving = projects.find((project) => project.id === workspaceProjectId)
          setProjectSwitch({
            fromName: leaving?.name ?? '',
            toName: target.name,
            toId: id,
            ...(leaving?.role ? { fromRole: leaving.role } : {}),
            ...(target.role ? { toRole: target.role } : {}),
          })
        }}
      />

      {/* La cortina, hermana del taller y no hija: por dentro éste se recompone
          entero al cambiar de proyecto, y lo que tape ese cambio tiene que ser
          precisamente lo que no se recomponga con él.

          La `key` la vuelve a montar en cada cambio, que es lo que rearranca su
          guion; sin ella, dos cambios seguidos reutilizarían la misma cortina
          con los temporizadores del primero ya gastados. */}
      {/* Hermana de la cortina de cambio y montada a su lado, por lo mismo: el
          taller que hay debajo se recompone entero, así que lo que tape ese
          cambio no puede recomponerse con él. */}
      {/* «Esto está borrado»: se abre al pulsar un fantasma del selector. Es un
          diálogo y no una navegación porque al proyecto no se puede entrar —hay
          que decidir antes si se recupera—. */}
      {reviewingDeleted ? (
        <ConfirmDialog
          title={t('home.deleted.tooltip')}
          message={(() => {
            const left = reviewingDeleted.purgeAt
              ? Math.max(0, Math.ceil((new Date(reviewingDeleted.purgeAt).getTime() - Date.now()) / 86_400_000))
              : null
            return (
              <>
                <strong>{reviewingDeleted.name || t('home.deleted.unnamed')}</strong>{' '}
                {t('home.deleted.gone')}{' '}
                {/* El plazo, con clave propia para el singular: «1 días» delata
                    una traducción hecha con una plantilla y un número suelto. */}
                {left === null ? t('home.deleted.purgeSoon')
                  : left === 0 ? t('home.deleted.purgeToday')
                    : left === 1 ? t('home.deleted.purgeInOne')
                      : t('home.deleted.purgeInDays', { count: left })}
                {' '}{t('home.deleted.recoverable')}
              </>
            )
          })()}
          confirmLabel={t('home.deleted.recover')}
          confirmingLabel="Recuperando…"
          cancelLabel="Cerrar"
          onCancel={() => { setReviewingDeleted(null) }}
          onConfirm={() => {
            /* Se cierra y se cede a la cortina: la espera y su ceremonia son de
               ella, y dejar el diálogo con su propio «Recuperando…» sería contar
               lo mismo dos veces con dos aspectos distintos. */
            const target = reviewingDeleted
            setReviewingDeleted(null)
            setRestoring(target)
          }}
        />
      ) : null}

      {restoring ? (
        <ProjectRestoreOverlay
          key={restoring.id}
          projectName={restoring.name}
          onRestore={() => runRestore(restoring)}
          onSwap={() => { handleOpen(restoring.id) }}
          onDone={() => {
            setRestoring(null)
            showToast(t('home.restored'))
          }}
          onFailed={() => { setRestoring(null) }}
        />
      ) : null}

      {deleting ? (
        <ProjectDeleteOverlay
          key={deleting.id}
          projectName={deleting.name}
          nextProjectName={projects.find((project) => project.id !== deleting.id)?.name ?? ''}
          onDelete={() => runDelete(deleting)}
          onSwap={() => { goAfterDelete(deleting.id) }}
          onDone={() => { setDeleting(null) }}
          onFailed={() => { setDeleting(null) }}
        />
      ) : null}

      {projectSwitch ? (
        <ProjectSwitchOverlay
          key={projectSwitch.toId}
          fromName={projectSwitch.fromName}
          toName={projectSwitch.toName}
          fromRole={projectSwitch.fromRole}
          toRole={projectSwitch.toRole}
          onSwap={() => handleOpen(projectSwitch.toId, true)}
          onDone={() => setProjectSwitch(null)}
        />
      ) : null}
      {templateDataModal}
      {projectLimitModal}
      {startProjectFlows}
      </UnsavedChangesProvider>
    )
  }

  /* Mientras no se sabe si hay proyectos, no se enseña ninguna de las dos
     respuestas.

     Aquí abajo se pinta la pantalla de «empieza tu primer sitio», y la lista
     empieza vacía porque todavía no ha llegado: quien acababa de iniciar sesión
     con un proyecto de siempre veía uno o dos segundos de onboarding antes de
     que el efecto de arriba lo mandara a su panel. El comentario de abajo decía
     «con proyectos nunca se llega», y es cierto — pero sólo DESPUÉS de cargar.

     En ese hueco va la misma pantalla de arranque que acaba de estar puesta
     durante el inicio de sesión, así que no hay parpadeo: sigue lo que ya
     había. Sólo cubre el camino a la pantalla de inicio; Ajustes y Facturación
     no dependen de esta lista y se abren igual.

     `!workspaceProjectId` porque cuando la caché sí trae proyecto la decisión
     ya está tomada desde el primer render, y ahí no hay nada que esperar. */
  if (loading && view === 'home' && !workspaceProjectId) {
    return <BootScreen theme={theme} />
  }

  /* Sin ningún proyecto no hay panel que enseñar: todas las secciones del
     menú cuelgan de uno. En vez del marco a medio llenar que había aquí
     —un menú de dos entradas y una lista vacía— se pinta la pantalla que
     empieza un sitio, que es lo único que se puede hacer en ese estado. */
  return (
    <UnsavedChangesProvider value={registry}>
    {/* `--solo`: el mismo marco pero sin menú lateral, así que su tarjeta no
        tiene que dejarle hueco a la izquierda. */}
    <div className="dash-shell dash-shell--topbar dash-shell--solo">
      <DashboardTopbar
        mobileNavOpen={mobileNavOpen}
        onToggleMobileNav={() => setMobileNavOpen((open) => !open)}
        token={token}
        onHome={() => goView('home')}
      />

      {/* El menú de usuario, en la barra de arriba a la derecha.
          En el workspace vive al pie del menú lateral, y aquí no hay menú
          lateral: sin esto no habría forma de llegar a la cuenta, al tema ni a
          cerrar sesión desde esta pantalla. Va por el hueco que la barra ya
          expone para las acciones. */}
      <DashboardTopbarEnd>
        <DashboardUserMenu
          avatarUrl={avatarUrl}
          userName={userName}
          displayName={displayName}
          onOpenSettings={() => goView('settings')}
          onOpenPlans={() => goView('plans')}
          onOpenUsage={() => goView('usage')}
          onOpenBilling={() => goView('billing')}
          onOpenDomains={() => goView('domains')}
        />
      </DashboardTopbarEnd>

      {/* El mismo marco que el workspace: `.dash-main` es quien pinta la tarjeta
          grande —borde, radio y superficie propia— y quien deja la barra de
          arriba fuera de ella. Reutilizarlo es lo que hace que esta pantalla se
          vea como el resto del panel en vez de como otra aplicación. */}
      <main className="dash-main">
        <div className="dash-workspace-content">
          {view === 'settings' ? (
            <WorkspaceSettings session={session} projects={projects} />
          ) : view === 'billing' ? (
            <BillingLedger token={token} />
          ) : view === 'usage' ? (
            <AccountUsage token={token} projects={projects} onOpenPlans={() => goView('plans')} />
          ) : isAdmin && view === 'authCodes' ? (
            <AdminAuthCodes token={token} />
          ) : isAdmin && view === 'aiUnmet' ? (
            <AdminAiUnmet token={token} />
          ) : isAdmin && view === 'adminEmails' ? (
            <AdminEmails token={token} />
          ) : view === 'domains' ? (
            <AccountDomains
              token={token}
              onOpenProject={(id) => {
                if (blockUnsaved()) return
                setView('home')
                handleOpen(id)
                setWorkspaceSection('publish')
              }}
            />
          ) : view === 'plans' ? (
            /* Aquí está lo que antes no se podía: elegir plan sin tener ningún
               proyecto abierto. Como sección del taller no había forma. */
            <ProjectPlans token={token} onOpenBilling={() => goView('billing')} />
          ) : (
            <>
            <TeamInviteBanner token={token} onAccepted={(id, name, role) => { void enterAcceptedProject(id, name, role) }} />
            {/* Y los traspasos, con el de invitaciones: los dos son avisos que
                caducan y piden una respuesta. */}
            <TransferBanner token={token} />
            <StartProjectScreen
              onStart={startProject}
              onOpenPlans={() => goViewFromStart('plans')}
              userId={userId}
              token={token}
              flowOpen={aiModalOpen}
              startPrompt={pendingPrompt?.prompt}
              onStartPromptUsed={() => setPendingPrompt(null)}
              carried={carried}
              onDropCarried={dropSiteDesign}
            />
            {/* Debajo, por lo mismo de arriba. Y aquí importa más: a esta
                pantalla se llega muchas veces JUSTO de borrar el último. */}
            <DeletedProjectsBanner token={token} onRestored={() => window.location.reload()} />
            </>
          )}
        </div>
      </main>

      {/* Fuera de la tarjeta, y a propósito: lo que va dentro es lo que se hace
          aquí —elegir por dónde empezar— y esto es lo que hay que saber sobre
          el sitio en sí. Metido dentro competiría con las tres opciones, que
          son la única decisión de la pantalla.

          Sólo en esta pantalla. En el workspace la tarjeta llega al filo y se
          desplaza; un pie fijo debajo obligaría a cerrarla y a robarle alto a
          cada sección para repetir un aviso que ya se leyó al entrar. */}
      <DashboardFooter
        note={<><strong>{t('home.beta.label')}</strong> {t('home.beta.body')}</>}
      />

      {templateDataModal}

      {projectLimitModal}

      {startProjectFlows}
    </div>
    </UnsavedChangesProvider>
  )
}
